import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../../components/ui/Badge';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  Select,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { deletePmsTool } from '../../../services/mutations';
import { normalizePaginatedListResponse } from '../../../services/paginatedList';
import type { PmsTool } from './useData';

type BoardTab = 'active' | 'inactive' | 'all';
type CalibrationFilter = 'all' | 'required' | 'due' | 'ok';
type LookupRow = { id: number; code?: string | null; name?: string | null };

const viewTabs: Array<{
  id: BoardTab;
  label: string;
  icon: React.ReactNode;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'active',
    label: 'Active',
    icon: <CheckCircleIcon className="h-4 w-4" />,
    emptyTitle: 'No active tools',
    emptyHint: 'Active tools for vessels appear here.',
  },
  {
    id: 'inactive',
    label: 'Inactive',
    icon: <FolderIcon className="h-4 w-4" />,
    emptyTitle: 'No inactive tools',
    emptyHint: 'Deactivated tools appear here.',
  },
  {
    id: 'all',
    label: 'All',
    icon: <DocumentTextIcon className="h-4 w-4" />,
    emptyTitle: 'No tools yet',
    emptyHint: 'Register calibrated instruments and special tools used on board.',
  },
];

function listFrom<T>(rows: T[] | { data?: T[] } | undefined): T[] {
  return Array.isArray(rows) ? rows : rows?.data ?? [];
}

function vesselLabel(row: PmsTool) {
  const v = row.vessel as { vesselName?: string; name?: string } | null | undefined;
  if (!v && row.vesselId == null) return 'Shared';
  return v?.vesselName ?? v?.name ?? (row.vesselId != null ? `#${row.vesselId}` : 'Shared');
}

function recordStatusLabel(row: PmsTool) {
  const s = row.recordStatus as { name?: string; code?: string } | null | undefined;
  return s?.name ?? s?.code ?? '';
}

function recordStatusCode(row: PmsTool) {
  return String((row.recordStatus as { code?: string } | null)?.code ?? '').toUpperCase();
}

function isActive(row: PmsTool) {
  return row.isActive !== false;
}

function formatDate(value?: unknown) {
  if (value == null || value === '') return '';
  const raw = String(value);
  const d = new Date(raw.length === 10 ? `${raw}T00:00:00.000Z` : raw);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: raw.length === 10 ? 'UTC' : undefined,
  });
}

function calibrationExpiry(row: PmsTool): string | null {
  if (!row.calibrationRequired) return null;
  if (row.calibrationExpiryDate == null || row.calibrationExpiryDate === '') return null;
  return String(row.calibrationExpiryDate).slice(0, 10);
}

function isCalibrationDue(row: PmsTool) {
  const exp = calibrationExpiry(row);
  if (!exp) return false;
  return exp <= new Date().toISOString().slice(0, 10);
}

function sortValue(row: PmsTool, key: string): string | number {
  switch (key) {
    case 'code':
      return String(row.code ?? '').toLowerCase();
    case 'name':
      return String(row.name ?? '').toLowerCase();
    case 'vessel':
      return vesselLabel(row).toLowerCase();
    case 'toolType':
      return String(row.toolType ?? '').toLowerCase();
    case 'calibration':
      return calibrationExpiry(row) ?? (row.calibrationRequired ? '1' : '9');
    case 'status':
      return recordStatusLabel(row).toLowerCase();
    case 'createdAt':
      return row.createdAt ? String(row.createdAt) : '';
    default:
      return '';
  }
}

const List: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = { from: `${location.pathname}${location.search}` };
  const confirm = useConfirm();

  const [tab, setTab] = useState<BoardTab>('active');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [vesselId, setVesselId] = useState('');
  const [recordStatusId, setRecordStatusId] = useState('');
  const [calibrationFilter, setCalibrationFilter] = useState<CalibrationFilter>('all');

  type VesselOptionRow = { id: number; vesselName: string; isActive?: boolean };
  const { data: vesselsRaw, isLoading: vesselsLoading } = useSWR<
    VesselOptionRow[] | { data?: VesselOptionRow[] }
  >('/vessels?page=1&limit=500', fetcher, { revalidateOnFocus: false });
  const { data: statusesRaw } = useSWR<LookupRow[] | { data?: LookupRow[] }>(
    '/pms-record-statuses?page=1&limit=500',
    fetcher,
    { revalidateOnFocus: false },
  );

  const vesselOptions = useMemo(() => {
    const list = listFrom(vesselsRaw).filter((v) => v.isActive !== false);
    return list.map((v) => ({ value: String(v.id), label: v.vesselName }));
  }, [vesselsRaw]);

  const statusOptions = useMemo(() => {
    const list = listFrom(statusesRaw);
    return list.map((s) => ({ value: String(s.id), label: s.name ?? s.code ?? `#${s.id}` }));
  }, [statusesRaw]);

  const calibrationOptions = [
    { value: 'required', label: 'Calibration required' },
    { value: 'due', label: 'Calibration due' },
    { value: 'ok', label: 'Calibration OK' },
  ];

  const listUrl = useMemo(() => {
    const params = new URLSearchParams({ page: '1', limit: '500' });
    if (search.trim()) params.set('searchParams', search.trim());
    return `/pms-tools?${params.toString()}`;
  }, [search]);

  const { data, error, isLoading, mutate } = useSWR(listUrl, fetcher, {
    revalidateOnFocus: true,
  });

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'code', direction: 'asc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'code' || key === 'name' || key === 'vessel' || key === 'toolType' || key === 'status'
          ? 'asc'
          : key === 'calibration'
            ? 'asc'
            : 'desc',
    },
  );

  useEffect(() => {
    setPage(1);
  }, [search, vesselId, recordStatusId, calibrationFilter, tab, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [sortConfig.key, sortConfig.direction]);

  const baseRows = useMemo(() => {
    const normalized = normalizePaginatedListResponse<PmsTool>(data, 1, 500);
    return normalized.data.filter((row) => {
      if (vesselId && String(row.vesselId) !== vesselId) return false;
      if (recordStatusId && String(row.recordStatusId) !== recordStatusId) return false;
      if (calibrationFilter === 'required' && !row.calibrationRequired) return false;
      if (calibrationFilter === 'due' && !isCalibrationDue(row)) return false;
      if (calibrationFilter === 'ok') {
        if (!row.calibrationRequired) return false;
        if (isCalibrationDue(row)) return false;
      }
      return true;
    });
  }, [data, vesselId, recordStatusId, calibrationFilter]);

  const tabRows = useMemo(() => {
    if (tab === 'all') return baseRows;
    return baseRows.filter((row) => (tab === 'active' ? isActive(row) : !isActive(row)));
  }, [baseRows, tab]);

  const activeCount = useMemo(() => baseRows.filter((row) => isActive(row)).length, [baseRows]);
  const inactiveCount = useMemo(() => baseRows.filter((row) => !isActive(row)).length, [baseRows]);

  const sortedRows = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...tabRows].sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (typeof av === 'number' && typeof bv === 'number') {
        if (av === bv) return 0;
        return av < bv ? -dir : dir;
      }
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [tabRows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  const hasFilters = Boolean(
    search.trim() || vesselId || recordStatusId || calibrationFilter !== 'all',
  );

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setVesselId('');
    setRecordStatusId('');
    setCalibrationFilter('all');
  };

  const switchTab = (next: BoardTab) => {
    if (next === tab) return;
    setTab(next);
  };

  const activeTab = useMemo(() => viewTabs.find((t) => t.id === tab) ?? viewTabs[0], [tab]);

  const tabCounts: Record<BoardTab, number | undefined> = {
    active: activeCount,
    inactive: inactiveCount,
    all: baseRows.length,
  };

  const handleDelete = async (e: React.MouseEvent, row: PmsTool) => {
    e.stopPropagation();
    const label = row.name != null ? String(row.name) : row.code != null ? String(row.code) : `Tool #${row.id}`;
    const ok = await confirm({
      title: 'Delete tool',
      message: `Delete “${label}”?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deletePmsTool(row.id);
      await mutate();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      window.alert(msg || (err instanceof Error ? err.message : 'Failed to delete tool'));
    }
  };

  return (
    <GeneralList
      tabs={viewTabs.map(({ id, label, icon }) => ({
        id,
        label,
        icon,
        count: tabCounts[id],
      }))}
      activeTab={tab}
      onTabChange={(value) => switchTab(value as BoardTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search tools…',
        title: 'Searches tool code and name',
      }}
      actions={
        <Link
          to="/pms/pms-tools/new"
          state={returnState}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
            colors.primary,
          )}
        >
          <PlusIcon className="h-4 w-4" />
          New tool
        </Link>
      }
      filters={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[150px]">
            <Select
              value={vesselId}
              options={vesselOptions}
              placeholder={vesselsLoading ? 'Loading vessels…' : 'Vessel'}
              showClearButton
              searchPlaceholder="Search vessels…"
              onChange={(e) => setVesselId(String(e.target.value))}
            />
          </div>
          <div className="w-[140px]">
            <Select
              value={recordStatusId}
              options={statusOptions}
              placeholder="Record status"
              showClearButton
              onChange={(e) => setRecordStatusId(String(e.target.value))}
            />
          </div>
          <div className="w-[160px]">
            <Select
              value={calibrationFilter === 'all' ? '' : calibrationFilter}
              options={calibrationOptions}
              placeholder="Calibration"
              showClearButton
              onChange={(e) =>
                setCalibrationFilter((e.target.value || 'all') as CalibrationFilter)
              }
            />
          </div>
        </div>
      }
      filtersInline
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading tools…"
      isEmpty={!error && pageRows.length === 0}
      emptyState={{
        icon:
          tab === 'active' && !hasFilters ? (
            <DocumentTextIcon className="h-12 w-12" />
          ) : (
            <FolderIcon className="h-12 w-12" />
          ),
        title: hasFilters ? 'No matching tools' : activeTab.emptyTitle,
        hint: hasFilters ? 'Try adjusting filters or clearing the search.' : activeTab.emptyHint,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : (
          <Link
            to="/pms/pms-tools/new"
            state={returnState}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New tool
          </Link>
        ),
      }}
      banner={
        error ? (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            Could not load tools.
          </p>
        ) : null
      }
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className="w-[5.5rem] px-3 py-3 font-medium" />
            <GeneralListSortableTh sortKey="code" className="w-[24%]">
              Tool
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="vessel" className="hidden w-[14%] md:table-cell">
              Vessel
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="toolType" className="hidden w-[14%] sm:table-cell">
              Type
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="calibration" className="hidden w-[14%] md:table-cell">
              Calibration
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="status" className="w-[12%]">
              Status
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="createdAt" className="hidden w-[12%] lg:table-cell">
              Created
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => {
            const code = row.code != null ? String(row.code) : `TOOL #${row.id}`;
            const statusCode = recordStatusCode(row);
            const due = isCalibrationDue(row);
            const calLabel = !row.calibrationRequired
              ? 'Not required'
              : due
                ? `Due ${formatDate(row.calibrationExpiryDate)}`
                : formatDate(row.calibrationExpiryDate);
            return (
              <tr
                key={row.id}
                onClick={() => navigate(`/pms/pms-tools/${row.id}`, { state: returnState })}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                  colors.border,
                )}
              >
                <td className="px-3 py-3.5">
                  <div className="flex flex-nowrap items-center">
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={`/pms/pms-tools/${row.id}`}
                      state={returnState}
                      title="View"
                      aria-label="View tool"
                      className="rounded-lg p-1 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={`/pms/pms-tools/${row.id}/edit`}
                      state={returnState}
                      title="Edit"
                      aria-label="Edit tool"
                      className="rounded-lg p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-900"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                    <button
                      type="button"
                      title="Delete"
                      aria-label="Delete tool"
                      onClick={(e) => handleDelete(e, row)}
                      className="rounded-lg p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
                <td className="max-w-0 px-3 py-3.5">
                  <div className="min-w-0">
                    <p className={cn('truncate font-medium', colors.text)}>{code}</p>
                    <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>
                      {row.name != null ? String(row.name) : ''}
                    </p>
                    <p className={cn('mt-1 truncate text-xs md:hidden', colors.textMuted)}>
                      {[vesselLabel(row), row.toolType != null ? String(row.toolType) : null]
                        .filter((v) => Boolean(v))
                        .join(' · ') || ''}
                    </p>
                  </div>
                </td>
                <td
                  className={cn('hidden max-w-0 truncate px-3 py-3.5 md:table-cell', colors.textMuted)}
                  title={vesselLabel(row)}
                >
                  {vesselLabel(row)}
                </td>
                <td className={cn('hidden max-w-0 truncate px-3 py-3.5 sm:table-cell', colors.textMuted)}>
                  {row.toolType != null ? String(row.toolType) : ''}
                </td>
                <td className="hidden whitespace-nowrap px-3 py-3.5 md:table-cell">
                  {!row.calibrationRequired ? (
                    <span className={colors.textMuted}>Not required</span>
                  ) : (
                    <Badge variant={due ? 'danger' : 'success'} className="whitespace-nowrap">
                      {calLabel}
                    </Badge>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  <Badge
                    variant={
                      statusCode === 'ACTIVE' || statusCode === 'APPROVED'
                        ? 'success'
                        : statusCode === 'INACTIVE' || statusCode === 'ARCHIVED'
                          ? 'default'
                          : 'info'
                    }
                    className="whitespace-nowrap"
                  >
                    {recordStatusLabel(row)}
                  </Badge>
                </td>
                <td className={cn('hidden whitespace-nowrap px-3 py-3.5 lg:table-cell', colors.textMuted)}>
                  {formatDate(row.createdAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default List;
